import cx from 'clsx';
import * as React from 'react';

import { useIntersectionObserver } from '@/hooks/useIntersectionObserver';
import { isEqual } from '@/utils/lodash-alternatives';

import Asset from './Asset';
import styles from './AssetLayer.module.scss';

export interface ImageType {
  normal: string;
  retina?: string;
  modern?: {
    normal: string;
    retina?: string;
  };
}

export type sizedSourcesType = {
  min?: number;
  max?: number;
  src: ImageType;
}[];

interface ImageAsset {
  type: 'image';
  imageSrc?: ImageType;
  rounded?: 'all' | 'top' | 'none';
  sizedSources?: sizedSourcesType;
  alt?: string;
  isLazy?: boolean;
  loadingAnimation?: boolean;
  fallbackBackgroundColor?: string;
  imageAttributes?: {
    [key: string]: boolean | string | { [key: string]: string };
  };
}

interface VideoAsset {
  type: 'video';
  videoSrc?: string;
  rounded?: 'all' | 'top' | 'none';
  sizedSources?: sizedSourcesType;
  alt?: string;
  isLazy?: boolean;
  loadingAnimation?: boolean;
  fallbackBackgroundColor?: string;
  videoAttributes?: {
    [key: string]: boolean | string | { [key: string]: string };
  };
}

type AssetLayerProps = ImageAsset | VideoAsset;

const AssetLayer: React.FC<AssetLayerProps> = React.memo(
  props => {
    const {
      fallbackBackgroundColor = 'rgb(0, 20, 60)',
      rounded = 'all',
      isLazy,
    } = props;

    const { ref, intersected } = useIntersectionObserver(!isLazy);

    return (
      <div
        className={cx(styles.wrapper, {
          [styles.rounded]: rounded === 'all',
          [styles.roundedTop]: rounded === 'top',
        })}
        ref={ref}
      >
        <Asset
          {...props}
          intersected={intersected}
          fallbackBackgroundColor={fallbackBackgroundColor}
          attributes={
            props.type === 'image'
              ? props.imageAttributes
              : props.videoAttributes
          }
        />
      </div>
    );
  },
  (prevProps: any, nextProps: any) => isEqual(prevProps, nextProps)
);

export default AssetLayer;
